import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Coins, Search, RefreshCw, Download, CheckCircle2, Loader2,
  ArrowLeft, ArrowRight, Trash2,
} from "lucide-react";
import ConfirmDialog from "@/components/ConfirmDialog";

const PAGE_SIZE = 15;

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [confirmDel, setConfirmDel] = useState(null);   // ordine da eliminare

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/orders");
      setOrders(data);
      setPage(0);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return orders.filter((o) => {
      if (statusFilter && o.payment_status !== statusFilter) return false;
      if (q) {
        const s = q.toLowerCase();
        if (!(o.user?.email || "").toLowerCase().includes(s)
            && !(o.package_id || "").toLowerCase().includes(s)
            && !(o.session_id || "").toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [orders, q, statusFilter]);

  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  const kpi = useMemo(() => {
    const paid = orders.filter((o) => o.payment_status === "paid");
    return {
      total: orders.length,
      paid: paid.length,
      revenue: paid.reduce((s, o) => s + Number(o.amount || 0), 0),
      credits: paid.reduce((s, o) => s + Number(o.credits || 0), 0),
    };
  }, [orders]);

  const doDelete = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/admin/orders/${confirmDel.session_id}`);
      toast.success("Ordine eliminato");
      setConfirmDel(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore eliminazione");
    }
  };

  return (
    <div data-testid="admin-orders">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">Ordini</h1>
          <p className="text-sm text-[#4A4A4A] mt-1">Tutte le transazioni Stripe della piattaforma.</p>
        </div>
        <button onClick={load} data-testid="admin-orders-refresh" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#E8E4D9] bg-white text-xs font-semibold hover:bg-[#F5F2EA]">
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Aggiorna
        </button>
      </div>

      {/* KPI */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Ordini totali" value={kpi.total} />
        <Kpi label="Pagati" value={kpi.paid} highlight="success" />
        <Kpi label="Revenue (EUR)" value={`€ ${kpi.revenue.toFixed(2).replace(".", ",")}`} highlight="primary" />
        <Kpi label="Crediti venduti" value={kpi.credits} />
      </div>

      {/* Filters */}
      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-sm">
          <Search className="w-3.5 h-3.5 text-[#737373] absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }}
            placeholder="Cerca per email, pacchetto o session id…"
            data-testid="admin-orders-search"
            className="w-full pl-8 pr-3 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm"
          />
        </div>
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }} data-testid="admin-orders-status-filter"
          className="px-3 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm">
          <option value="">Tutti gli stati</option>
          <option value="paid">Pagati</option>
          <option value="pending">Pending</option>
          <option value="failed">Falliti</option>
          <option value="expired">Scaduti</option>
          <option value="refunded">Rimborsati</option>
        </select>
      </div>

      {/* Table */}
      <div className="mt-4 rounded-2xl border border-[#E8E4D9] bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F5F2EA] border-b border-[#E8E4D9] text-[10px] uppercase tracking-[0.15em] text-[#737373] font-bold">
              <th className="text-left px-4 py-2.5">Data</th>
              <th className="text-left px-4 py-2.5">Utente</th>
              <th className="text-left px-4 py-2.5">Pacchetto</th>
              <th className="text-left px-4 py-2.5 w-20">Crediti</th>
              <th className="text-left px-4 py-2.5 w-28">Importo</th>
              <th className="text-left px-4 py-2.5 w-28">Stato</th>
              <th className="text-left px-4 py-2.5 w-32">Proforma</th>
              <th className="text-right px-4 py-2.5 w-32">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="p-6 text-center text-[#737373]">Caricamento…</td></tr>}
            {!loading && paged.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-[#737373] italic">Nessun ordine trovato.</td></tr>
            )}
            {!loading && paged.map((o, i) => (
              <tr key={o.session_id} className={i > 0 ? "border-t border-[#E8E4D9]/70" : ""} data-testid={`admin-order-row-${i}`}>
                <td className="px-4 py-2.5 font-mono text-xs">{String(o.created_at || "").slice(0, 19).replace("T", " ")}</td>
                <td className="px-4 py-2.5">
                  <div className="text-xs font-medium">{o.user?.email || "—"}</div>
                  <div className="text-[10px] text-[#737373]">{o.user?.full_name}</div>
                </td>
                <td className="px-4 py-2.5 font-medium">{o.package_id}</td>
                <td className="px-4 py-2.5 tabular-nums">{o.credits}</td>
                <td className="px-4 py-2.5 tabular-nums">€ {Number(o.amount || 0).toFixed(2).replace(".", ",")}</td>
                <td className="px-4 py-2.5">
                  {o.payment_status === "paid" ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded uppercase tracking-wider">
                      <CheckCircle2 className="w-3 h-3" /> Pagato
                    </span>
                  ) : (
                    <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded uppercase tracking-wider">
                      {o.payment_status}
                    </span>
                  )}
                </td>
                <td className="px-4 py-2.5">
                  {o.payment_status === "paid" ? (
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${o.session_id}/proforma`}
                      target="_blank" rel="noreferrer"
                      data-testid={`admin-order-proforma-${i}`}
                      className="inline-flex items-center gap-1 text-[10px] font-bold text-[#1D4ED8] hover:underline"
                    >
                      <Download className="w-3 h-3" /> {o.proforma_number || "PDF"}
                    </a>
                  ) : <span className="text-[10px] text-[#A3A3A3]">—</span>}
                </td>
                <td className="px-4 py-2.5 text-right">
                  <button
                    onClick={() => setConfirmDel(o)}
                    data-testid={`admin-order-delete-${i}`}
                    title="Elimina ordine"
                    className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-3 h-3" /> Elimina
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="mt-4 flex items-center justify-between text-xs">
          <div className="text-[#737373]">{page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)} su {filtered.length}</div>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#E8E4D9] bg-white disabled:opacity-40 font-semibold">
              <ArrowLeft className="w-3 h-3" /> Precedente
            </button>
            <span className="tabular-nums font-semibold">Pag. {page + 1} / {pages}</span>
            <button onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} disabled={page + 1 >= pages} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#E8E4D9] bg-white disabled:opacity-40 font-semibold">
              Successiva <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmDel}
        onOpenChange={(v) => !v && setConfirmDel(null)}
        onConfirm={doDelete}
        icon={Trash2}
        tone="danger"
        title={`Eliminare ordine di ${confirmDel?.user?.email || "—"}?`}
        description={`Verrà rimosso definitivamente il record di pagamento (${confirmDel?.package_id || "pacchetto"} · € ${Number(confirmDel?.amount || 0).toFixed(2).replace(".", ",")}) e la relativa fattura di piattaforma se generata. Il saldo crediti dell'utente NON verrà toccato: usa Utenti › Modifica crediti per stornarli manualmente. Operazione irreversibile.`}
        confirmLabel="Sì, elimina ordine"
      />
    </div>
  );
}

function Kpi({ label, value, highlight }) {
  const c = highlight === "success" ? "text-emerald-700 bg-emerald-50 border-emerald-200"
    : highlight === "primary" ? "text-[#1D4ED8] bg-blue-50 border-blue-200"
    : "text-[#0A0A0A] bg-white border-[#E8E4D9]";
  return (
    <div className={`rounded-2xl border ${c} p-4`}>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-70">{label}</div>
      <div className="font-display font-extrabold text-2xl mt-1 tabular-nums">{value}</div>
    </div>
  );
}
